import { mapKeys } from 'lodash';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Masks, formatWithMask } from 'react-native-mask-input';
import { Button, Layout, Text } from '../../../components/core';
import { FormErrorSubmit, FormInput, FormInputMasked, FormSelect } from '../../../components/form';
import { PageError } from '../../../components/page';
import { OnDemandUserQuery, StateCodes, useUpdateHubUserMutation } from '../../../graphQL';
import { getRoute, useNavigate } from '../../../routes';
import { getStylesheet } from '../../../styles';
import { secondary } from '../../../styles/colors';
import { DATE_REGEX, convertDateForDisplay } from '../../../utils/date';

type ConfirmContactFormProps = {
  hubUser: OnDemandUserQuery['currentHubUser'];
  nextRoute: 'onDemandPreEncounterForm' | 'findCareDemographics';
};

const PHONE_FORMAT = [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

const uppercasedStateCodes = mapKeys(StateCodes, value => value.toUpperCase());

export function ConfirmContactForm({ hubUser, nextRoute }: ConfirmContactFormProps): JSX.Element {
  const [pageError, setPageError] = useState(false);
  const [update] = useUpdateHubUserMutation({
    onCompleted: () => {
      navigate(getRoute(nextRoute, {}));
    },
    onError: () => {
      setPageError(true);
    },
  });
  const { preferredName, firstName, lastName, phone, birthDate, pronouns, primaryAddress } =
    hubUser;

  const hasPhoneNumber = Boolean(phone);
  const hasBirthDate = Boolean(birthDate);
  const hasFirstName = Boolean(firstName);
  const hasLastName = Boolean(lastName);

  const navigate = useNavigate();

  const formContext = useForm({
    defaultValues: {
      preferredName: preferredName ?? '',
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      phone: convertPhoneNumberForDisplay(phone) ?? '',
      birthDate: convertDateForDisplay(birthDate) ?? '',
      pronouns: pronouns ?? '',
      line1: primaryAddress?.line1 ?? '',
      line2: primaryAddress?.line2 ?? '',
      city: primaryAddress?.city ?? '',
      state: primaryAddress?.state ?? null,
      zip: primaryAddress?.zip ?? '',
      submit: undefined,
    },
  });

  const {
    clearErrors,
    control,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = formContext;

  const submitForm = (): void => {
    clearErrors();

    void handleSubmit(async (updates): Promise<void> => {
      const unmaskedPhoneNumber = formatWithMask({
        text: updates.phone,
        mask: PHONE_FORMAT,
      }).unmasked;

      const { line1, line2, city, state, zip, ...baseUpdates } = updates;

      void update({
        variables: {
          updates: {
            ...baseUpdates,
            birthDate: convertBirthDateForUpdate(updates.birthDate),
            phone: unmaskedPhoneNumber,
            primaryAddress: {
              line1,
              line2,
              city,
              state,
              zip,
            },
          },
        },
      });
    })();
  };

  if (pageError) {
    return <PageError />;
  }

  return (
    <FormProvider {...formContext}>
      <Layout.VStack space={5}>
        <Layout.View>
          <FormInput
            label="Preferred Name"
            name="preferredName"
            placeholder="Preferred Name"
            autoCapitalize="none"
            autoFocus
            control={control}
            error={errors.preferredName}
            necessityIndicator
            blurOnSubmit={false}
            returnKeyType="next"
            onSubmitEditing={() => setFocus('firstName')}
          />
          <Text.paraSmall marginTop={2} color="secondary.600">
            What should we call you?
          </Text.paraSmall>
        </Layout.View>

        <FormInput
          label="Legal First Name"
          name="firstName"
          placeholder="First Name"
          autoCapitalize="none"
          control={control}
          error={errors.firstName}
          isRequired
          isDisabled={hasFirstName}
          necessityIndicator
          blurOnSubmit={false}
          returnKeyType="next"
          onSubmitEditing={() => setFocus('lastName')}
        />

        <FormInput
          label="Legal Last Name"
          name="lastName"
          placeholder="Last Name"
          autoCapitalize="none"
          control={control}
          error={errors.lastName}
          isRequired
          isDisabled={hasLastName}
          necessityIndicator
          blurOnSubmit={false}
          returnKeyType="next"
          onSubmitEditing={() => setFocus('pronouns')}
        />

        <Layout.View>
          <FormInput
            label="Pronouns"
            name="pronouns"
            placeholder="Pronouns"
            autoCapitalize="none"
            control={control}
            error={errors.pronouns}
            necessityIndicator
            blurOnSubmit={false}
            returnKeyType="next"
            onSubmitEditing={() => {
              if (!hasPhoneNumber) {
                setFocus('phone');
              } else if (!hasBirthDate) {
                setFocus('birthDate');
              } else setFocus('line1');
            }}
          />
          <Text.paraSmall marginTop={2} color={secondary[600]}>
            Examples: she/her, he/him, they/them, she/they, he/they, xe/xim, she/he/they
          </Text.paraSmall>
        </Layout.View>

        <Layout.View>
          <FormInputMasked
            label="Phone Number"
            name="phone"
            mask={PHONE_FORMAT}
            placeholder="###-###-####"
            control={control}
            error={errors.phone}
            isRequired
            isDisabled={hasPhoneNumber}
            necessityIndicator
            rules={{ minLength: 12 }}
            blurOnSubmit={false}
            returnKeyType="next"
            onSubmitEditing={() => {
              if (hasBirthDate) setFocus('line1');
              else setFocus('birthDate');
            }}
          />

          <Text.paraSmall marginTop={2} color="secondary.600">
            Provide a number where we can reach you if necessary.
          </Text.paraSmall>
        </Layout.View>
        <FormInputMasked
          label="Date of Birth"
          mask={Masks.DATE_MMDDYYYY}
          name="birthDate"
          placeholder="MM/DD/YYYY"
          autoCapitalize="none"
          control={control}
          error={errors.birthDate}
          isRequired
          isDisabled={hasBirthDate}
          necessityIndicator
          rules={{
            pattern: {
              value: DATE_REGEX,
              message: 'You must enter a valid date MM/DD/YYYY.',
            },
          }}
          blurOnSubmit={false}
          returnKeyType="next"
          onSubmitEditing={() => setFocus('line1')}
        />
        <Layout.VStack space={2}>
          <FormInput
            label="Where are you currently living?"
            name="line1"
            placeholder="Address Line 1"
            autoCapitalize="none"
            control={control}
            error={errors.line1}
            isRequired
            necessityIndicator
            blurOnSubmit={false}
            returnKeyType="next"
            onSubmitEditing={() => setFocus('line2')}
          />

          <FormInput
            name="line2"
            placeholder="Address Line 2"
            autoCapitalize="none"
            control={control}
            error={errors.line2}
            blurOnSubmit={false}
            returnKeyType="next"
            onSubmitEditing={() => setFocus('city')}
          />
          <FormInput
            name="city"
            placeholder="City"
            autoCapitalize="none"
            control={control}
            error={errors.city}
            isRequired
            blurOnSubmit={false}
            returnKeyType="next"
            onSubmitEditing={() => setFocus('zip')}
          />

          <Layout.HStack {...styles.stack}>
            <FormSelect
              name="state"
              options={uppercasedStateCodes}
              placeholder="State"
              control={control}
              error={errors.state}
              necessityIndicator
              isRequired
            />

            <FormInput
              name="zip"
              placeholder="Zip"
              autoCapitalize="none"
              control={control}
              error={errors.zip}
              rules={{
                required: 'Zip code is required',
                pattern: {
                  value: /^\d{5}(?:[-\s]\d{4})?$/,
                  message: 'Invalid zip code',
                },
              }}
              isRequired
              inputMode="numeric"
            />
          </Layout.HStack>
        </Layout.VStack>

        {errors.submit && <FormErrorSubmit marginTop={6}>{errors.submit.message}</FormErrorSubmit>}

        <Layout.View marginTop={8}>
          <Button.primaryLarge testID="button-patient-info-submit" onPress={submitForm}>
            Confirm and continue
          </Button.primaryLarge>
        </Layout.View>
      </Layout.VStack>
    </FormProvider>
  );
}

const convertBirthDateForUpdate = (birthDate?: string | null): string => {
  if (birthDate === null || birthDate === undefined) return '';

  const birthDateArray = birthDate.split('/');
  const year = birthDateArray[2] ?? '';
  const month = birthDateArray[0] ?? '';
  const day = birthDateArray[1] ?? '';

  return `${year}-${month}-${day}`;
};

const convertPhoneNumberForDisplay = (phoneNumber?: string | null): string => {
  if (phoneNumber === null || phoneNumber === undefined) return '';

  const areaCode = phoneNumber.slice(0, 3);
  const firstThree = phoneNumber.slice(3, 6);
  const lastFour = phoneNumber.slice(6, 10);

  return `${areaCode}-${firstThree}-${lastFour}`;
};

const styles = getStylesheet({
  stack: {
    width: '50%',
  },
});
